import { API_BASE_URL, isChatApiConfigured } from '../config';

/**
 * EF-Soft's legal identity (data controller, address, registry numbers,
 * supervisory authority, governing law). Per the 03/10/2026 data-placement
 * rule it is operator data owned by the control plane and is never compiled
 * into this bundle: the ChatServe API proxies it anonymously at
 * GET api/restaurant/platform-legal-info (the website's origins are in the
 * API's CORS list). Every field is empty until the operator fills the row in
 * or when the API is not configured; the legal pages then hide the block.
 */
export interface PlatformLegalInfo {
  controllerName: string;
  addressLines: string[];
  contactEmail: string;
  phone: string;
  commercialRegister: string;
  vatId: string;
  supervisoryAuthority: string;
  hostingRegion: string;
  logRetentionDays: number | null;
  governingLaw: string;
  isPopulated: boolean;
}

export const EMPTY_PLATFORM_LEGAL_INFO: PlatformLegalInfo = {
  controllerName: '',
  addressLines: [],
  contactEmail: '',
  phone: '',
  commercialRegister: '',
  vatId: '',
  supervisoryAuthority: '',
  hostingRegion: '',
  logRetentionDays: null,
  governingLaw: '',
  isPopulated: false,
};

type Raw = Record<string, unknown>;

// Tolerant read: camelCase or PascalCase, and counsel placeholders count as empty.
const readString = (raw: Raw, camel: string): string => {
  const pascal = camel.charAt(0).toUpperCase() + camel.slice(1);
  const value = raw[camel] ?? raw[pascal];
  const text = typeof value === 'string' ? value.trim() : '';
  return /[⟨⟩<>]|LEGAL:/.test(text) ? '' : text;
};

export const mapPlatformLegalInfo = (body: unknown): PlatformLegalInfo => {
  const raw: Raw = body && typeof body === 'object' && !Array.isArray(body) ? (body as Raw) : {};
  const cityLine = [readString(raw, 'addressPostalCode'), readString(raw, 'addressCity')]
    .filter(Boolean)
    .join(' ');
  const retention = Number(raw.logRetentionDays ?? raw.LogRetentionDays);
  const controllerName = readString(raw, 'controllerName');

  return {
    controllerName,
    addressLines: [readString(raw, 'addressStreet'), cityLine, readString(raw, 'addressCountry')].filter(Boolean),
    contactEmail: readString(raw, 'contactEmail'),
    phone: readString(raw, 'phone'),
    commercialRegister: readString(raw, 'commercialRegister'),
    vatId: readString(raw, 'vatId'),
    supervisoryAuthority: readString(raw, 'supervisoryAuthority'),
    hostingRegion: readString(raw, 'hostingRegion'),
    logRetentionDays: Number.isFinite(retention) && retention > 0 ? Math.floor(retention) : null,
    governingLaw: readString(raw, 'governingLaw'),
    isPopulated: controllerName !== '',
  };
};

let cached: Promise<PlatformLegalInfo> | null = null;

/** One request per page load, shared by every legal page; failures resolve to the empty record. */
export const loadPlatformLegalInfo = (): Promise<PlatformLegalInfo> => {
  if (!isChatApiConfigured) return Promise.resolve(EMPTY_PLATFORM_LEGAL_INFO);
  if (!cached) {
    cached = fetch(`${API_BASE_URL}restaurant/platform-legal-info`, { mode: 'cors' })
      .then((response) => (response.ok ? response.json() : null))
      .then(mapPlatformLegalInfo)
      .catch(() => {
        cached = null; // allow a later page to retry
        return EMPTY_PLATFORM_LEGAL_INFO;
      });
  }
  return cached;
};
